import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { blogService } from '../../services/db';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import SEO from '../../components/common/SEO';
import transparentLogo from '../../assets/images/Transparent_logo.png';
import { PLAY_STORE_URL } from '../../constants';
import {
  GridCategoryIcon,
  BibleIcon,
  SproutIcon,
  FaithCrossIcon,
  CalendarIcon,
  ClockIcon,
} from '../../components/common/Icons';

const DEFAULT_CATEGORIES = ['All', 'Scripture Study', 'Youth Ministry', 'Faith & Growth'];

const CATEGORY_META = {
  All: {
    icon: <GridCategoryIcon size={14} style={{ color: 'var(--clr-primary)' }} />,
    label: 'All'
  },
  'Scripture Study': {
    icon: <BibleIcon size={14} style={{ color: '#C08552' }} />,
    label: 'Scripture Study'
  },
  'Youth Ministry': {
    icon: <SproutIcon size={14} style={{ color: '#059669' }} />,
    label: 'Youth Ministry'
  },
  'Faith & Growth': {
    icon: <FaithCrossIcon size={14} style={{ color: '#2563EB' }} />,
    label: 'Faith & Growth'
  },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

const Blog = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [posts, setPosts] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPost, setSelectedPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadPosts = async () => {
      setIsLoading(true);
      try {
        const allPosts = await blogService.getAllPosts(false);
        setPosts(allPosts);
        if (slug) {
          let match = allPosts.find((p) => p.slug === slug || p.id === slug);
          if (!match) {
            match = await blogService.getPostBySlug(slug);
          }
          if (match) setSelectedPost(match);
        } else {
          setSelectedPost(null);
        }
      } catch (err) {
        console.error('Failed to load blog posts:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadPosts();
  }, [slug]);

  // Dynamically compute active category list
  const categories = useMemo(() => {
    const set = new Set(DEFAULT_CATEGORIES);
    posts.forEach((p) => {
      if (p.category) set.add(p.category);
    });
    return Array.from(set);
  }, [posts]);

  const handleSelectPost = (post) => {
    setSelectedPost(post);
    navigate(`/blog/${post.slug || post.id}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToList = () => {
    setSelectedPost(null);
    navigate('/blog');
  };

  const filteredPosts = posts.filter((post) => {
    const matchesCat = activeCategory === 'All' || post.category === activeCategory;
    const matchesSearch = !searchQuery.trim() || 
      post.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCat && matchesSearch;
  });

  return (
    <div className="blog-page">
      {selectedPost ? (
        <SEO
          title={`${selectedPost.title} | Bible Contest Blog`}
          description={selectedPost.excerpt || 'Read this uplifting article on Bible Contest Blog.'}
          keywords={`Bible blog, Christian articles, Scripture study, ${selectedPost.category || ''}, Christian faith`}
          canonicalUrl={`/blog/${selectedPost.slug || selectedPost.id}`}
          ogType="article"
          schema={{
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": selectedPost.title,
            "description": selectedPost.excerpt || '',
            "articleSection": selectedPost.category || 'Scripture Study',
            "datePublished": selectedPost.publishedAt || new Date().toISOString(),
            "author": {
              "@type": "Person",
              "name": selectedPost.author || 'Bible Contest Editorial Team'
            },
            "publisher": {
              "@type": "Organization",
              "name": "Bible Contest Ministry",
              "logo": {
                "@type": "ImageObject",
                "url": "https://biblecontest.app/android-chrome-512x512.png"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://biblecontest.app/blog/${selectedPost.slug || selectedPost.id}`
            }
          }}
        />
      ) : (
        <SEO
          title="Biblical Insights Blog — Daily Scripture Reading & Study Habits"
          description="Read uplifting articles on building daily Bible study habits, student winner spotlights, scripture memorization strategies, and contest updates."
          keywords="Bible blog, Christian articles, Scripture reading habit, Bible trivia blog, Sunday school articles, Christian youth devotion"
          canonicalUrl="/blog"
          schema={{
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Biblical Insights Blog",
            "description": "Uplifting articles on building daily Scripture reading habits and student spotlights.",
            "url": "https://biblecontest.app/blog",
            "isPartOf": {
              "@type": "WebSite",
              "name": "Bible Contest",
              "url": "https://biblecontest.app/"
            }
          }}
        />
      )}

      {selectedPost ? (
        /* Full Article Reader Mode */
        <article className="article-reader card">
          <button
            type="button"
            className="btn btn--ghost btn--sm back-btn"
            onClick={handleBackToList}
            style={{ marginBottom: '1.5rem', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            ← Back to All Articles
          </button>

          <header className="article-header">
            <div className="article-meta">
              <Badge label={selectedPost.category || 'Scripture Study'} variant="primary" />
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <ClockIcon size={14} style={{ color: 'var(--clr-muted)' }} />
                <span>{selectedPost.readTime || '3 min read'}</span>
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <CalendarIcon size={14} style={{ color: 'var(--clr-primary)' }} />
                <span>{formatDate(selectedPost.publishedAt)}</span>
              </span>
            </div>
            <h1 className="article-title">{selectedPost.title}</h1>
            <p className="article-excerpt">{selectedPost.excerpt}</p>
            <div className="article-author-badge">
              <div className="author-avatar" style={{ overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src={transparentLogo} alt="" style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
              </div>
              <div>
                <strong>{selectedPost.author || 'Bible Contest Editorial Team'}</strong>
                <small>Bible Contest Ministry</small>
              </div>
            </div>
          </header>

          {selectedPost.coverImage && (
            <div className="article-cover-wrapper">
              <img
                src={selectedPost.coverImage}
                alt={selectedPost.title}
                className="article-cover-img"
              />
            </div>
          )}

          <div className="article-body">
            {selectedPost.content.split('\n\n').map((paragraph, idx) => {
              const text = paragraph.trim();
              if (text.startsWith('### ')) {
                return <h3 key={idx}>{text.replace('### ', '')}</h3>;
              }
              if (text.startsWith('> ')) {
                return (
                  <blockquote key={idx} className="article-quote">
                    {text.replace('> ', '')}
                  </blockquote>
                );
              }
              if (text.startsWith('---')) {
                return <hr key={idx} className="article-divider" />;
              }
              if (text.startsWith('1. ') || text.startsWith('- ')) {
                const items = text.split('\n');
                return (
                  <ul key={idx} className="article-list">
                    {items.map((item, i) => (
                      <li key={i}>{item.replace(/^(\d+\. |- )/, '')}</li>
                    ))}
                  </ul>
                );
              }
              return <p key={idx}>{text}</p>;
            })}
          </div>

          <footer className="article-footer">
            <div className="share-box">
              <h4>Let God's Word Take Root in Your Heart</h4>
              <p style={{ color: 'var(--clr-text-2)', fontSize: '14px', lineHeight: 1.5, margin: '6px 0 16px' }}>
                Turn what you read into lifelong Scripture memory with daily interactive quizzes, student rewards, and friendly church leagues!
              </p>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <Button
                  variant="primary"
                  onClick={() => window.open(PLAY_STORE_URL, '_blank')}
                  style={{ minHeight: '46px' }}
                >
                  Start Daily Quizzes on Google Play →
                </Button>
                <Button variant="secondary" onClick={handleBackToList} style={{ minHeight: '46px' }}>
                  Read More Insights
                </Button>
              </div>
            </div>
          </footer>
        </article>
      ) : (
        /* Blog Index / Listing Mode */
        <>
          <section className="blog-hero">
            <span className="hero-pill" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <BibleIcon size={14} style={{ color: 'var(--clr-primary)' }} />
              <span>NOURISH YOUR SPIRIT</span>
            </span>
            <h1 className="blog-hero__title">
              Deepen Your Faith, Master the Word & Grow Together
            </h1>
            <p className="blog-hero__subtitle">
              Uplifting teachings, practical Bible study methods, student success spotlights, and sound biblical insights to help you cherish God's Word every day.
            </p>

            {/* Modern Search & Category Filter Section */}
            <div className="blog-filter-section">
              {/* Sleek Search Bar */}
              <div className="blog-search-box">
                <svg
                  className="blog-search-icon"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  placeholder="Search articles, study methods, devotionals..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="blog-search-input"
                  aria-label="Search articles"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="blog-search-clear"
                    aria-label="Clear Search"
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Category Pill Tags Track */}
              <div className="blog-category-track" role="tablist" aria-label="Article categories">
                {categories.map((cat) => {
                  const count = cat === 'All'
                    ? posts.length
                    : posts.filter((p) => p.category === cat).length;
                  const meta = CATEGORY_META[cat] || {
                    icon: <GridCategoryIcon size={14} style={{ color: 'var(--clr-primary)' }} />,
                    label: cat
                  };
                  const isActive = activeCategory === cat;

                  return (
                    <button
                      key={cat}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      className={`blog-category-chip ${isActive ? 'active' : ''}`}
                      onClick={() => setActiveCategory(cat)}
                    >
                      <span className="chip-icon">{meta.icon}</span>
                      <span className="chip-text">{cat}</span>
                      <span className="chip-count">{count}</span>
                    </button>
                  );
                })}
              </div>

              {/* Active search or category feedback summary */}
              {(searchQuery || activeCategory !== 'All') && (
                <div className="blog-filter-feedback">
                  <span>
                    Showing <strong>{filteredPosts.length}</strong> {filteredPosts.length === 1 ? 'article' : 'articles'}
                    {activeCategory !== 'All' && <> in <em>{activeCategory}</em></>}
                    {searchQuery && <> matching &ldquo;<strong>{searchQuery}</strong>&rdquo;</>}
                  </span>
                  <button
                    type="button"
                    className="blog-filter-reset-btn"
                    onClick={() => {
                      setActiveCategory('All');
                      setSearchQuery('');
                    }}
                  >
                    Reset filters
                  </button>
                </div>
              )}
            </div>
          </section>

          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--clr-muted)' }}>
              Loading articles...
            </div>
          ) : filteredPosts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--clr-muted)', background: '#FFFFFF', borderRadius: '16px', border: '1px solid var(--clr-border)' }}>
              No articles found matching your query.
            </div>
          ) : (
            <section className="blog-grid">
              {filteredPosts.map((post) => (
                <div
                  key={post.id || post.slug}
                  className="blog-card"
                  onClick={() => handleSelectPost(post)}
                >
                  <div className="blog-card__img-wrapper">
                    {post.coverImage ? (
                      <img
                        src={post.coverImage}
                        alt={post.title}
                        className="blog-card__img"
                        loading="lazy"
                      />
                    ) : (
                      <div className="blog-card__fallback-img">
                        <BibleIcon size={32} style={{ color: 'var(--clr-primary)', marginBottom: '4px' }} />
                        <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                          Bible Contest Insights
                        </span>
                      </div>
                    )}
                    <Badge
                      label={post.category || 'Scripture Study'}
                      variant="primary"
                      style={{ position: 'absolute', top: '12px', left: '12px', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}
                    />
                  </div>

                  <div className="blog-card__content">
                    <div className="blog-card__meta">
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                        <ClockIcon size={13} style={{ color: 'var(--clr-muted)' }} />
                        <span>{post.readTime || '3 min'}</span>
                      </span>
                      <span>•</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                        <CalendarIcon size={13} style={{ color: 'var(--clr-primary)' }} />
                        <span>{formatDate(post.publishedAt)}</span>
                      </span>
                    </div>

                    <h3 className="blog-card__title">{post.title}</h3>
                    <p className="blog-card__excerpt">{post.excerpt}</p>

                    <div className="blog-card__footer">
                      <span className="read-more-link">Read Full Article →</span>
                    </div>
                  </div>
                </div>
              ))}
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default Blog;
